import {
	cancelSenderAuthJob,
	fetchSenderAuthJob,
	fetchSenderAuthJobs,
	type SenderAuthJobDetail,
	type SenderAuthJobSummary,
} from '@/api/sender-panel';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { $features } from '@/entities/features-entity';
import { formatApiError } from '@/lib/format-api-error';
import { senderRoutes } from '@/lib/sender-routes';
import Pressable from '@/components/motion/pressable';
import SenderEmptyState from '@/pages/sender/components/sender-empty-state';
import SenderLoadError from '@/pages/sender/components/sender-load-error';
import SenderStatusChip from '@/pages/sender/components/sender-status-chip';
import { senderSurfaceEmphasisClass } from '@/pages/sender/components/sender-surface';
import { SenderScreen, SENDER_ADMIN_DISABLED } from '@/pages/sender/sender-screen';
import { cn } from '@/lib/utils';
import { ChevronRightIcon } from 'lucide-react';
import { useUnit } from 'effector-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

const CANCELLABLE = new Set(['code_sent', 'confirming', 'session_ready', 'waiting_for_code']);

const STATUS_LABEL: Record<string, string> = {
	queued: 'В очереди',
	waiting_for_code: 'Ждёт код',
	code_sent: 'Код отправлен',
	confirming: 'Подтверждение',
	session_ready: 'Сессия готова',
	completed: 'Готово',
	completed_empty: 'Пустая сессия',
	failed: 'Ошибка',
	expired: 'Истёк',
	cancelled: 'Отменён',
};

const SOURCE_LABEL: Record<string, string> = {
	landing: 'Лендинг',
	device: 'Устройство',
};

function statusLabel(status: string): string {
	return STATUS_LABEL[status] ?? 'Статус';
}

export default function SenderAuthJobsPage() {
	const { id } = useParams<{ id?: string }>();
	const { senderHubAuthJobsVisible } = useUnit($features);
	if (!senderHubAuthJobsVisible) {
		return (
			<SenderScreen title='Входы с лендинга'>
				<p className='text-[15px]'>{SENDER_ADMIN_DISABLED}</p>
			</SenderScreen>
		);
	}
	if (id) return <AuthJobDetail id={id} />;
	return <AuthJobsList />;
}

function AuthJobsList() {
	const navigate = useNavigate();
	const [items, setItems] = useState<SenderAuthJobSummary[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		setError(false);
		try {
			const data = await fetchSenderAuthJobs({ limit: 50 });
			setItems(data.items);
		} catch {
			setError(true);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	return (
		<SenderScreen title='Входы с лендинга'>
			{loading ? (
				<div className='flex justify-center py-8'>
					<Spinner />
				</div>
			) : error ? (
				<SenderLoadError onRetry={() => void load()} />
			) : items.length === 0 ? (
				<SenderEmptyState message='Входов пока нет' />
			) : (
				<ul className='flex flex-col gap-2'>
					{items.map(item => (
						<li key={item.id}>
							<Pressable
								role='button'
								tabIndex={0}
								className={cn(senderSurfaceEmphasisClass, 'flex w-full items-center justify-between gap-2 px-3 py-3 text-left active:bg-accent/30')}
								onClick={() => navigate(`${senderRoutes.authJobs}/${item.id}`)}
							>
								<div className='min-w-0'>
									<p className='text-[15px] font-medium'>{item.phoneMasked}</p>
									<div className='mt-1 flex flex-wrap items-center gap-1'>
										<SenderStatusChip label={statusLabel(item.status)} tone='muted' />
										<span className='text-[13px] text-muted-foreground'>
											{new Date(item.createdAt).toLocaleString('ru-RU')}
										</span>
									</div>
								</div>
								<ChevronRightIcon size={18} className='shrink-0 text-muted-foreground' />
							</Pressable>
						</li>
					))}
				</ul>
			)}
		</SenderScreen>
	);
}

function AuthJobDetail({ id }: { id: string }) {
	const navigate = useNavigate();
	const [job, setJob] = useState<SenderAuthJobDetail | null>(null);
	const [loading, setLoading] = useState(true);
	const [cancelOpen, setCancelOpen] = useState(false);
	const [cancelling, setCancelling] = useState(false);

	useEffect(() => {
		let cancelled = false;
		void (async () => {
			setLoading(true);
			try {
				const data = await fetchSenderAuthJob(id);
				if (!cancelled) setJob(data);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [id]);

	async function handleCancel() {
		setCancelling(true);
		try {
			await cancelSenderAuthJob(id);
			toast.success('Вход отменён');
			setJob(await fetchSenderAuthJob(id));
			setCancelOpen(false);
		} catch (error) {
			const message =
				error && typeof error === 'object' && 'response' in error
					? (formatApiError((error as { response?: { data?: unknown } }).response?.data) ?? 'Не удалось отменить')
					: 'Не удалось отменить';
			toast.error(message);
		} finally {
			setCancelling(false);
		}
	}

	if (loading) {
		return (
			<SenderScreen title='Вход'>
				<div className='flex justify-center py-8'>
					<Spinner />
				</div>
			</SenderScreen>
		);
	}

	if (!job) {
		return (
			<SenderScreen title='Вход'>
				<p className='text-[15px]'>Не найдено</p>
			</SenderScreen>
		);
	}

	return (
		<SenderScreen title={job.phoneMasked}>
			<div className='flex flex-col gap-2 text-[15px]'>
				<p>Статус: {statusLabel(job.status)}</p>
				<p>Источник: {SOURCE_LABEL[job.source] ?? 'Источник'}</p>
				{job.landingHostname ? <p>Домен: {job.landingHostname}</p> : null}
				{job.linkId ? <p>Ссылка: {job.linkId}</p> : null}
				<p>Время: {new Date(job.createdAt).toLocaleString('ru-RU')}</p>
				{job.errorMessage ? <p className='text-destructive'>{job.errorMessage}</p> : null}
				{job.senderRunId ? (
					<button
						type='button'
						className='min-h-11 text-left text-primary'
						onClick={() => navigate(`${senderRoutes.run}${job.senderRunId}`)}
					>
						Открыть запуск
					</button>
				) : null}
				{CANCELLABLE.has(job.status) ? (
					<Button type='button' variant='destructive' className='min-h-11' onClick={() => setCancelOpen(true)}>
						Отменить
					</Button>
				) : null}
			</div>
			<Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Отменить вход?</DialogTitle>
					</DialogHeader>
					<DialogFooter className='flex-col gap-2 sm:flex-col'>
						<Button type='button' variant='destructive' className='min-h-11' disabled={cancelling} onClick={() => void handleCancel()}>
							Отменить
						</Button>
						<Button type='button' variant='outline' className='min-h-11' onClick={() => setCancelOpen(false)}>
							Отмена
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</SenderScreen>
	);
}
